import { useLayoutEffect, useState, useCallback, useRef } from "react";
export function useRTLDetection() {
    const [direction, setDirection] = useState('ltr');
    const [element, setElement] = useState(null);
    const cachedDirection = useRef('ltr');
    const observerRef = useRef(null);
    const detectDirection = useCallback((targetElement) => {
        if (typeof window === 'undefined')
            return 'ltr';
        const docDir = document.documentElement.dir;
        if (docDir === 'rtl' || docDir === 'ltr')
            return docDir;
        if (getComputedStyle(document.documentElement).direction === 'rtl')
            return 'rtl';
        let current = targetElement;
        while (current) {
            const dir = current.dir || current.getAttribute('dir');
            if (dir === 'rtl' || dir === 'ltr')
                return dir;
            if (current === targetElement || current === document.body) {
                if (getComputedStyle(current).direction === 'rtl')
                    return 'rtl';
            }
            current = current.parentElement;
        }
        return 'ltr';
    }, []);
    const ref = useCallback((node) => {
        setElement(node);
    }, []);
    useLayoutEffect(() => {
        const newDirection = detectDirection(element);
        if (newDirection !== cachedDirection.current) {
            cachedDirection.current = newDirection;
            setDirection(newDirection);
        }
    });
    useLayoutEffect(() => {
        if (typeof window === 'undefined')
            return;
        observerRef.current?.disconnect();
        const observer = new MutationObserver(() => {
            const newDirection = detectDirection(element);
            if (newDirection !== cachedDirection.current) {
                cachedDirection.current = newDirection;
                setDirection(newDirection);
            }
        });
        observerRef.current = observer;
        const config = {
            attributes: true,
            attributeFilter: ['dir']
        };
        const targets = [document.documentElement];
        if (document.body) {
            targets.push(document.body);
        }
        if (element) {
            targets.push(element);
            if (element.parentElement) {
                targets.push(element.parentElement);
            }
        }
        targets.forEach(target => observer.observe(target, config));
        return () => {
            observer.disconnect();
        };
    }, [element, detectDirection]);
    return [direction, ref];
}
